/**
 * ADR-0214 Part 2 revision 3 — Activity: what people are working on (screen
 * spec §3B.7, as approved).
 *
 * A 3×3 grid of the nine cards. Each card has a title, a "Ranked by …" line
 * and up to five rows: a name over a bar whose length is relative to the
 * card's top row. No numbers on the dashboard; the bar's colour carries the
 * meaning. The whole card opens its detail screen. Admins only for now.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { rangeLastDays } from "../components/AiUsageDashboard.js";
import { DateRangeToolbar } from "../components/SubjectTables.js";
import { apiFetch } from "../lib/api.js";
import { trailOnward } from "../lib/activityTrail.js";
import {
  ACTION_DEFINITION,
  AUDIENCE_LABEL,
  PURPOSE_LABEL,
  barFill,
  barWidth,
  rangeQuery,
  type ActivityDashboardResponse,
  type ActivityScopeView,
  type Purpose,
  type Tone,
} from "../lib/activity.js";

const PURPOSES: readonly Purpose[] = ["business", "development", "both"];

/** One choice among a few, as buttons: the chosen one highlighted (owner, 2026-10-19: no dropdowns). */
function ChoiceButtons<T extends string>(props: { label: string; value: T; options: ReadonlyArray<{ value: T; label: string }>; onPick: (v: T) => void }) {
  return (
    <div role="group" aria-label={props.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span className="label">{props.label}</span>
      <div style={{ display: "inline-flex", gap: 4 }}>
        {props.options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={o.value === props.value ? "btn btnPrimary" : "btn"}
            aria-pressed={o.value === props.value}
            onClick={() => props.onPick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * From, To, Show (Mine / My people / Everyone, as the hub offers this person)
 * and Work (administrators, on every audience), on one line. Shared by the
 * dashboard and its detail screens. "Gone quiet after N days" lives on the
 * Gone quiet detail screen (owner, 2026-10-19).
 */
export function ActivityToolbar(props: { from: string; to: string; scope: ActivityScopeView | null; onChange: (next: Record<string, string>) => void }) {
  const scope = props.scope;
  return (
    <DateRangeToolbar from={props.from} to={props.to} onChange={(r) => props.onChange(r)}>
      {scope ? (
        <ChoiceButtons
          label="Show"
          value={scope.audience}
          options={scope.audiences.map((a) => ({ value: a, label: AUDIENCE_LABEL[a] }))}
          onPick={(audience) => props.onChange({ audience })}
        />
      ) : null}
      {scope?.purpose_choice ? (
        <ChoiceButtons label="Work" value={scope.purpose} options={PURPOSES.map((p) => ({ value: p, label: PURPOSE_LABEL[p] }))} onPick={(purpose) => props.onChange({ purpose })} />
      ) : null}
    </DateRangeToolbar>
  );
}

/** The period and quiet window, from the address (default the last 30 days, 7 days). */
export function useActivityRange() {
  const [params, setParams] = useSearchParams();
  const fallback = rangeLastDays(30);
  const from = params.get("from") || fallback.from;
  const to = params.get("to") || fallback.to;
  const quietDays = params.get("quiet_days") || "7";
  // Empty: the hub's default for this person (My people when they have anyone below them, else Mine; business work).
  const audience = params.get("audience") || "";
  const purpose = params.get("purpose") || "";
  const set = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) p.set(k, v);
    setParams(p, { replace: true });
  };
  /** The address keys as they would be after `set(next)`, for a screen that moves elsewhere on a change. */
  const queryWith = (next: Record<string, string>) =>
    rangeQuery(next.from ?? from, next.to ?? to, next.quiet_days ?? quietDays, next.audience ?? audience, next.purpose ?? purpose);
  return { from, to, quietDays, audience, purpose, set, queryWith, query: rangeQuery(from, to, quietDays, audience, purpose) };
}

/** A name over a bar. */
export function NameBar(props: { name: ReactNode; value: number; top: number; tone: Tone; title?: string }) {
  return (
    <div style={{ display: "grid", gap: 2, minWidth: 0 }} title={props.title}>
      <div style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{props.name}</div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--border)" }}>
        <div style={{ height: 6, borderRadius: 3, width: barWidth(props.value, props.top), background: barFill(props.tone) }} />
      </div>
    </div>
  );
}

export function TenantActivity() {
  const range = useActivityRange();
  const [data, setData] = useState<ActivityDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiFetch<ActivityDashboardResponse>(`/api/tenant/activity?${range.query}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [range.query]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout title="Activity" description={ACTION_DEFINITION}>
      <div style={{ display: "grid", gap: 12 }}>
        <ActivityToolbar from={range.from} to={range.to} scope={data} onChange={range.set} />

        {error ? <ErrorState detail={error} /> : null}
        {!error && !data ? <LoadingState /> : null}

        {!error && data ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(max(240px, calc((100% - 24px) / 3)), 1fr))", gap: 12 }}>
            {data.cards.map((c) => {
              const top = c.rows[0]?.value ?? 0;
              return (
                <Link
                  key={c.key}
                  to={`/app/activity/${c.key}?${range.query}`}
                  className="card"
                  style={{ padding: "12px 14px", display: "grid", gap: 8, alignContent: "start", color: "inherit", textDecoration: "none" }}
                  aria-label={`${c.title}: open the detail`}
                  state={trailOnward([], { to: `/app/activity?${range.query}`, label: "Activity" })}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.title}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {c.by}
                    </div>
                  </div>
                  {c.rows.length === 0 ? (
                    <div className="muted" style={{ fontSize: 13 }}>
                      {c.empty}
                    </div>
                  ) : (
                    c.rows.map((r, i) => (
                      <NameBar
                        key={`${r.kind ?? ""}:${r.name}:${i}`}
                        name={r.kind ? <>{r.name} <span className="muted">({r.kind})</span></> : r.name}
                        value={r.value}
                        top={top}
                        tone={c.tone}
                      />
                    ))
                  )}
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
